/**
 * Geometric constants of the generated PDF, mirrored from the PDF microservice
 * (`hustro_pdf_microservice` `PdfConfig` / `pdfCreator`) and calibrated against a set
 * of reference reports (single issue rendered as full-A4 / miniatures / list, plus a
 * multi-level issue list). All values are in PDF points (1/72 inch), A4 portrait.
 *
 * The estimator does not call the backend or the PDF microservice; it replays the
 * same layout/pagination rules client-side to count pages.
 */

export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

export const PAGE_MARGIN = {
  top: 24,
  bottom: 48,
  left: 32,
  right: 32,
} as const;

export const CONTENT_WIDTH =
  PAGE_WIDTH - PAGE_MARGIN.left - PAGE_MARGIN.right; // 531

/**
 * Project label header drawn at the top of every page. The body content starts below
 * it. Calibrated effective height (label text + spacing).
 */
export const HEADER_HEIGHT = 18;

/** First Y at which body content is drawn on a page. */
export const CONTENT_TOP = PAGE_MARGIN.top + HEADER_HEIGHT; // 42

/** Y below which content may no longer be drawn (footer + bottom margin reserved). */
export const CONTENT_BOTTOM = PAGE_HEIGHT - PAGE_MARGIN.bottom; // 794

/** Vertical space usable for body content on a single page. */
export const USABLE_HEIGHT = CONTENT_BOTTOM - CONTENT_TOP; // 752

export const FONT_SIZE = {
  label: 8,
  content: 9,
  sectionTitle: 12,
  reportTitle: 14,
} as const;

/**
 * jsPDF `getTextDimensions` uses a 1.15 line-height factor; `Text.calculateHeight`
 * additionally pads by `fontSize * 0.15`. Combined ≈ `fontSize * 1.3` per line.
 */
export const LINE_HEIGHT_FACTOR = 1.3;

/** Rich text (HTML) wraps with the bare 1.15 factor and no extra padding. */
export const RICH_LINE_HEIGHT_FACTOR = 1.15;

/**
 * Average glyph advance as a fraction of font size for the report font (Archivo).
 * Calibrated so that wrapped long-text fields (description) match the reference PDFs.
 */
export const AVG_CHAR_WIDTH_FACTOR = 0.5;

/** Horizontal divider: 0.5pt line + ~12pt vertical margin. */
export const DIVIDER_HEIGHT = 13;

/** Image height caps from `PdfConfig`. */
export const MAX_FULL_IMAGE_HEIGHT = 550;
export const MAX_MINIATURE_IMAGE_HEIGHT = 160;
export const MAX_CROPPED_MAP_HEIGHT = 257;

/** Number of miniature attachment cells per row. */
export const MINIATURES_PER_ROW = 3;
